import { useState } from "react";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";

const details = [
  {
    icon: Mail,
    label: "Email",
    value: "c4plumbingheating@hotmail.com",
    href: "mailto:c4plumbingheating@hotmail.com",
    testid: "contact-email",
  },
  {
    icon: Phone,
    label: "Phone",
    value: "07402 288 642",
    href: "tel:+447402288642",
    testid: "contact-phone",
  },
  {
    icon: MapPin,
    label: "Based In",
    value: "Warsash, Southampton",
    testid: "contact-area",
  },
  {
    icon: MapPin,
    label: "Coverage",
    value: "All of Hampshire & parts of Dorset",
    testid: "contact-coverage",
  },
  {
    icon: Clock,
    label: "Hours",
    value: "Mon – Fri · 8:00 – 17:00",
    testid: "contact-hours",
  },
];

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.message.length < 5) {
      toast.error("Please complete all fields.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message sent. We&apos;ll be in touch shortly.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Couldn't send message", {
        description: err?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 lg:py-32 bg-white border-t border-[#E8E5DD]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <span className="uppercase text-xs tracking-[0.3em] text-[#C9A24A] font-semibold">
            Get in Touch
          </span>
          <h2
            data-testid="contact-title"
            className="mt-4 font-heading text-3xl lg:text-5xl text-[#1C2A39] font-medium tracking-tight"
          >
            Prefer a quick chat first?
          </h2>
          <p className="mt-6 text-base text-[#5C6670] leading-relaxed max-w-md">
            Drop us a line — we&apos;re happy to talk through your ideas, share
            recent project examples, or arrange a free site visit.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-px bg-[#E8E5DD]">
            {details.map((d) => {
              const Wrap = d.href ? "a" : "div";
              return (
                <Wrap
                  key={d.label}
                  href={d.href}
                  data-testid={d.testid}
                  className="bg-white p-6 hover:bg-[#F9F8F6] transition-colors block"
                >
                  <d.icon
                    size={18}
                    strokeWidth={1.5}
                    className="text-[#C9A24A]"
                  />
                  <div className="mt-3 font-heading text-xs uppercase tracking-[0.18em] text-[#5C6670]">
                    {d.label}
                  </div>
                  <div className="mt-1 font-heading text-sm text-[#1C2A39] font-semibold">
                    {d.value}
                  </div>
                </Wrap>
              );
            })}
          </div>

          <div
            data-testid="contact-map-placeholder"
            className="mt-8 aspect-[16/9] w-full bg-[#F9F8F6] border border-dashed border-[#E8E5DD] grid place-items-center"
          >
            <div className="text-center px-6">
              <MapPin
                size={28}
                strokeWidth={1.25}
                className="text-[#C9A24A] mx-auto"
              />
              <div className="mt-3 text-xs uppercase tracking-[0.2em] text-[#5C6670]">
                Based in
              </div>
              <div className="mt-1 font-heading text-sm text-[#1C2A39] font-semibold">
                Warsash, Southampton
              </div>
              <div className="mt-2 text-xs text-[#5C6670]">
                Covering all of Hampshire &amp; parts of Dorset
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="bg-[#F9F8F6] p-7 lg:p-10 border border-[#E8E5DD] space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="font-heading text-xs uppercase tracking-[0.18em] text-[#1C2A39]">
                  Name
                </label>
                <Input
                  data-testid="contact-input-name"
                  className="mt-2 rounded-sm border-[#E8E5DD] bg-white h-11 focus-visible:ring-[#C9A24A]"
                  value={form.name}
                  onChange={(e) =>
                    setForm({ ...form, name: e.target.value })
                  }
                  placeholder="Your name"
                  required
                />
              </div>
              <div>
                <label className="font-heading text-xs uppercase tracking-[0.18em] text-[#1C2A39]">
                  Email
                </label>
                <Input
                  data-testid="contact-input-email"
                  type="email"
                  className="mt-2 rounded-sm border-[#E8E5DD] bg-white h-11 focus-visible:ring-[#C9A24A]"
                  value={form.email}
                  onChange={(e) =>
                    setForm({ ...form, email: e.target.value })
                  }
                  placeholder="you@example.co.uk"
                  required
                />
              </div>
            </div>
            <div>
              <label className="font-heading text-xs uppercase tracking-[0.18em] text-[#1C2A39]">
                Message
              </label>
              <Textarea
                data-testid="contact-input-message"
                rows={6}
                className="mt-2 rounded-sm border-[#E8E5DD] bg-white focus-visible:ring-[#C9A24A] resize-none"
                value={form.message}
                onChange={(e) =>
                  setForm({ ...form, message: e.target.value })
                }
                placeholder="How can we help?"
                required
              />
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                data-testid="contact-submit-button"
                disabled={loading}
                className="bg-[#1C2A39] hover:bg-[#2A3D52] text-white rounded-sm h-12 px-7 font-heading font-semibold tracking-wide"
              >
                {loading ? "Sending..." : (
                  <>
                    Send message
                    <Send size={16} strokeWidth={1.75} className="ml-2" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
